import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import React, { useRef } from 'react'
import {
  FieldArrayPath, FieldPathByValue, FieldValues, PathValue, useFormContext,
} from 'react-hook-form'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { getCountryFilePolicy } from 'shared/file/models/fileTypePolicy'
import { excludeDeleted } from 'shared/lib/common-util'
import FileService from 'routes/Declaration/services/file.service'

type FileArrayPath<TForm extends FieldValues> = Extract<FieldArrayPath<TForm>, FieldPathByValue<TForm, FileType[]>>

interface DocumentDropdownAreaProps<TForm extends FieldValues> {
  documentId: number | null
  maxSize?: number
  name: FileArrayPath<TForm>
  country: string
}

function DocumentDropdownArea<
  TForm extends FieldValues,
>({
  name, documentId, country, maxSize,
}: DocumentDropdownAreaProps<TForm>) {
  const { setValue, getValues } = useFormContext<TForm>()
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)

  const isFilenameUnique = (filename: string) => getValues(name)
    .filter((file: FileType) => !file.deleted && (file.fileName?.trim() === filename)).length === 0

  const generateNameForUploadedFile = (originalFilename: string) => {
    let filename = originalFilename.trim()
    let isUnique = isFilenameUnique(filename)
    if (isUnique) {
      return filename
    }

    let depth = 1
    while (!isUnique) {
      filename = FileService.addDuplicationNumberToName(originalFilename, depth)
      isUnique = isFilenameUnique(filename)
      if (!isUnique) {
        // eslint-disable-next-line no-plusplus
        depth++
      }
    }

    return filename
  }

  const addNewFiles = async (droppedFiles: FileList) => {
    const policy = getCountryFilePolicy(country)
    const currentValues: FileType[] = getValues(name)
    const changedFiles = currentValues.length === 0 ? [] : [...currentValues.filter((file) => !(file.deleted && file.id === null))]
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < droppedFiles.length; i++) {
      const file = droppedFiles.item(i)

      if (!file) {
        toast.error('Error accessing file')
      } else if (file.size === 0) {
        toast.error(`File '${file.name}' size is invalid (0KB), please upload the correct file`)
      } else if (file.size > policy.maxBytes) {
        toast.error(`File '${file.name}' size is too large. Maximum allowed size - ${policy.maxBytes / 1024 / 1024} MiB`)
      } else {
        const fileName = generateNameForUploadedFile(file.name)
        if (fileName.length > policy.maxFileNameLength) {
          toast.error(`File ${file.name} name is too long. Maximum ${policy.maxFileNameLength} characters allowed`)
          return
        }
        if (!policy.isAcceptedFileType({ fileName: file.name, mimeType: file.type })) {
          const { key, options } = policy.invalidFileTypeMessage
          toast.error(t(key, options))
          return
        }
        // eslint-disable-next-line no-await-in-loop
        const fileBytes = await file.arrayBuffer()
        const fileBlob = fileBytes !== undefined ? new Blob([fileBytes], { type: file.type }) : null

        const newFile: FileType = {
          id: null,
          sequenceNumber: changedFiles.length,
          deleted: false,
          uuid: null,
          mimeType: file.type,
          url: null,
          fileName,
          fileBlob,
          file,
          documentId: documentId ?? null,
        }

        changedFiles.push(newFile)
        setValue(name, changedFiles as PathValue<TForm, typeof name>, {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    }
  }

  const isDropAreaVisible = maxSize !== undefined
    ? getValues(name).filter(excludeDeleted).length < maxSize : true

  return (
    <div className="row mb-3" key={`documentBodyDropArea_${name}`}>
      <div className="col-12">
        {
          !isDropAreaVisible ? null : (
            <div
              role="presentation"
              className="dropdown-area bg-white"
              onClick={() => {
                if (fileRef.current) {
                  fileRef.current.click()
                }
              }}
              onDragOver={(event) => {
                event.currentTarget.classList.add('file-hover')
                event.preventDefault()
              }}
              onDragLeave={(event) => {
                event.currentTarget.classList.remove('file-hover')
              }}
              onDropCapture={(event) => {
                event.currentTarget.classList.remove('file-hover')

                addNewFiles(event.dataTransfer.files)

                event.preventDefault()
                event.stopPropagation()
              }}
            >
              <input
                className="d-none"
                type="file"
                ref={fileRef}
                onChange={(event) => {
                  if (event.target.files) {
                    addNewFiles(event.target.files)
                  }
                  event.preventDefault()
                  event.stopPropagation()
                }}
              />
              <i className="fal fa-cloud-upload me-2" />
              <span>
                {t('buttons.addFiles')}
              </span>
            </div>
          )
        }
      </div>
    </div>
  )
}

DocumentDropdownArea.defaultProps = {
  maxSize: undefined,
}

export default DocumentDropdownArea
